import { Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type BigNumber from "bignumber.js";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import type { MortgagePatchBody } from "shared";
import { z } from "zod";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { NumberInput } from "@/components/form/NumberInput";
import { QueryKeys } from "@/LinkGen";
import { useMortgage } from "@/mortgages/hooks";

export type EscrowKeys = keyof Pick<
  MortgagePatchBody,
  "monthlyMortgageInsurance" | "monthlyPropertyInsurance"
>;

interface EditMortgageEscrowDialogProps {
  mortgageID: number;
  fieldKey: EscrowKeys;
}

const title: Record<EscrowKeys, string> = {
  monthlyMortgageInsurance: "Edit monthly mortgage insurance",
  monthlyPropertyInsurance: "Edit monthly property insurance",
};

const field: Record<EscrowKeys, string> = {
  monthlyMortgageInsurance: "Monthly mortgage insurance",
  monthlyPropertyInsurance: "Monthly property insurance",
};

const defaultValue: Record<
  EscrowKeys,
  (mortgage: ReturnType<typeof useMortgage>) => BigNumber | null
> = {
  monthlyMortgageInsurance: (mortgage) =>
    mortgage.data.escrowBreakdown.mortgageInsurance,
  monthlyPropertyInsurance: (mortgage) =>
    mortgage.data.escrowBreakdown.propertyInsurance,
};

export const EditMortgageEscrowDialog = (
  props: EditMortgageEscrowDialogProps,
) => {
  const mortgage = useMortgage(props.mortgageID);
  const dialog = useDialog();

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(
      z.object({
        amount: z.coerce.number().nonnegative("Amount cannot be negative"),
      }),
    ),
    defaultValues: {
      amount: defaultValue[props.fieldKey](mortgage)?.toNumber() ?? 0,
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (values) => {
    await Api.MortgagePatch(props.mortgageID, {
      [props.fieldKey]: values.amount,
    });
    await queryClient.refetchQueries({
      queryKey: QueryKeys.Mortgage(props.mortgageID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>{title[props.fieldKey]}</Dialog.Header>
      <Dialog.Body>
        <NumberInput
          label={field[props.fieldKey]}
          placeholder={field[props.fieldKey]}
          autoFocus
          control={control}
          name="amount"
          required
        />
      </Dialog.Body>
      <Dialog.Body>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Body>
    </form>
  );
};
